import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SpecialtiesSection = () => {
  // ✅ Dental Service Schema WITH ADDRESS
  const dentalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "MDeRCM Dental Practice RCM Services",
    "description": "Specialized revenue cycle management services for dental practices including orthodontics, oral surgery, and general dentistry",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
      "addressLocality": "Marlton",
      "addressRegion": "NJ",
      "postalCode": "08053",
      "addressCountry": "US"
    },
    "serviceType": "Dental Practice Revenue Cycle Management",
    "areaServed": "United States",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dental RCM Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Automated Dental Pre-authorizations"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI-based CDT Code Validation"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Dental Insurance Verification"
          }
        }
      ]
    }
  };

  // ✅ Mental Health Service Schema WITH ADDRESS
  const mentalHealthServiceSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "MDeRCM Mental Health Practice RCM",
    "description": "Expert medical billing outsourcing services for behavioral health, psychiatry, therapy practices, and substance abuse treatment centers",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
      "addressLocality": "Marlton",
      "addressRegion": "NJ",
      "postalCode": "08053",
      "addressCountry": "US"
    },
    "serviceType": "Mental Health Practice Revenue Cycle Management",
    "areaServed": "United States",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Mental Health RCM Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Session-based Billing Automation"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "ICD-10 Mental Health Coding"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Telehealth Billing Support"
          }
        }
      ]
    }
  };

  return (
    <section
      style={{
        padding: '80px 5%',
        backgroundColor: '#f2f7fb',
        fontFamily: "'Poppins', sans-serif",
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'clip',
      }}
      itemScope
      itemType="https://schema.org/ItemList"
      role="region"
      aria-label="Specialty healthcare revenue cycle management services"
    >
      {/* ✅ Add Schemas to Head */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(dentalServiceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(mentalHealthServiceSchema)}
        </script>
      </Helmet>

      {/* ✅ SEO-OPTIMIZED H2 */}
      <motion.h2
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        style={{
          textAlign: 'center',
          fontSize: 'clamp(1.9rem, 5vw, 2.8rem)',
          fontWeight: '700',
          marginBottom: '25px',
          color: '#1A1A1A',
          lineHeight: '1.3',
        }}
        itemProp="name"
      >
        Revenue Cycle Management Services for Hospitals & Specialty Clinics
      </motion.h2>

      {/* Supporting paragraph with keywords */}
      <p style={{
        fontSize: '1.1rem',
        textAlign: 'center',
        maxWidth: '900px',
        margin: '0 auto 50px',
        lineHeight: '1.7',
        color: '#444',
      }}
      itemProp="description"
      >
        Our specialized{' '}
        <Link 
          to="/why" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Learn about medical billing and coding outsourcing"
        >
          medical billing and coding outsourcing
        </Link>{' '}
        services are tailored for dental and mental health providers. As one of the <strong>best revenue cycle management companies</strong>, we understand the unique billing challenges faced by specialty practices.
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '35px',
          justifyContent: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        {/* ✅ Dental Card - ENHANCED */}
        <motion.div
          initial={{ opacity: 0, x: -50, rotateY: -10 }}
          whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          style={{
            flex: '1 1 450px',
            minWidth: '300px',
            maxWidth: '500px',
            backgroundColor: '#ffffff',
            borderRadius: '14px',
            padding: '35px 30px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            textAlign: 'center',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.3s ease',
            border: '1px solid #e8e8e8',
          }}
          whileHover={{
            scale: 1.04,
            rotateY: 2,
            boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
          }}
          itemScope
          itemType="https://schema.org/Service"
        >
          <div style={{ fontSize: '3rem', marginBottom: '20px' }} aria-hidden="true">🦷</div>
          
          {/* H3 for Dental */}
          <h3 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            marginBottom: '20px',
            color: '#0077b6'
          }}
          itemProp="name"
          >
            Dental Practice RCM Services
          </h3>
          
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.7',
            color: '#555',
            marginBottom: '25px',
          }}
          itemProp="description"
          >
            Comprehensive <strong>revenue cycle management services for hospitals</strong> specializing in dental care, including orthodontics, oral surgery, and general dentistry.
          </p>

          <ul style={{ 
            listStyle: 'none', 
            padding: 0, 
            lineHeight: '2', 
            fontSize: '1.02rem', 
            color: '#333',
            textAlign: 'left',
            marginBottom: '25px'
          }}>
            <li>✓ Automated dental pre-authorizations</li>
            <li>✓ AI-based CDT code validation</li>
            <li>✓ Procedure-to-code accuracy mapping</li>
            <li>✓ Clean claim submission to dental insurers</li>
            <li>✓ Insurance verification & eligibility checks</li>
            <li>✓ Patient payment plan management</li>
          </ul>

          {/* CTA Button */}
          <Link 
            to="/Dental"
            style={{
              display: 'inline-block',
              padding: '12px 28px',
              backgroundColor: '#0077b6',
              color: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#005f8a';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#0077b6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            aria-label="Learn more about dental practice RCM services"
          >
            Learn More About Dental RCM →
          </Link>

          {/* Stats Box */}
          <div style={{
            marginTop: '25px',
            padding: '15px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            textAlign: 'left',
          }}>
            <h4 style={{
              fontSize: '0.95rem',
              fontWeight: '700',
              color: '#2e7d32',
              marginBottom: '10px',
            }}>
              💡 Dental Practice Results:
            </h4>
            <ul style={{
              fontSize: '0.85rem',
              lineHeight: '1.6',
              color: '#555',
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              <li>• 35% faster claim processing</li>
              <li>• 98% clean claim rate</li>
              <li>• 40% reduction in denials</li>
            </ul>
          </div>
        </motion.div>

        {/* ✅ Mental Health Card - ENHANCED */}
        <motion.div
          initial={{ opacity: 0, x: 50, rotateY: 10 }}
          whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          style={{
            flex: '1 1 450px',
            minWidth: '300px',
            maxWidth: '500px',
            backgroundColor: '#ffffff',
            borderRadius: '14px',
            padding: '35px 30px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            textAlign: 'center',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.3s ease',
            border: '1px solid #e8e8e8',
          }}
          whileHover={{
            scale: 1.04,
            rotateY: -2,
            boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
          }}
          itemScope
          itemType="https://schema.org/Service"
        >
          <div style={{ fontSize: '3rem', marginBottom: '20px' }} aria-hidden="true">🧠</div>
          
          {/* H3 for Mental Health */}
          <h3 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            marginBottom: '20px',
            color: '#0077b6'
          }}
          itemProp="name"
          >
            Mental Health Practice RCM
          </h3>
          
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.7',
            color: '#555',
            marginBottom: '25px',
          }}
          itemProp="description"
          >
            Expert <strong>medical billing outsourcing services</strong> for behavioral health, psychiatry, therapy practices, and substance abuse treatment centers.
          </p>

          <ul style={{ 
            listStyle: 'none', 
            padding: 0, 
            lineHeight: '2', 
            fontSize: '1.02rem', 
            color: '#333',
            textAlign: 'left',
            marginBottom: '25px'
          }}>
            <li>✓ Session-based billing automation</li>
            <li>✓ ICD-10 mental health code suggestions</li>
            <li>✓ Therapy-to-claim workflow automation</li>
            <li>✓ Payer rules & logic pre-built</li>
            <li>✓ Telehealth billing support</li>
            <li>✓ Prior authorization management</li>
          </ul>

          {/* CTA Button */}
          <Link 
            to="/MentalHealth"
            style={{
              display: 'inline-block',
              padding: '12px 28px',
              backgroundColor: '#0077b6',
              color: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#005f8a';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#0077b6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            aria-label="Learn more about mental health practice RCM services"
          >
            Learn More About Mental Health RCM →
          </Link>

          {/* Stats Box */}
          <div style={{
            marginTop: '25px',
            padding: '15px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            textAlign: 'left',
          }}>
            <h4 style={{
              fontSize: '0.95rem',
              fontWeight: '700',
              color: '#2e7d32',
              marginBottom: '10px',
            }}>
              💡 Mental Health Practice Results:
            </h4>
            <ul style={{
              fontSize: '0.85rem',
              lineHeight: '1.6',
              color: '#555',
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              <li>• 50% faster telehealth billing</li>
              <li>• 45% reduction in denials</li>
              <li>• 30% increase in collections</li>
            </ul>
          </div>
        </motion.div>
      </div>

      {/* Additional Services Info */}
      <div style={{
        textAlign: 'center',
        marginTop: '50px',
        padding: '30px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        maxWidth: '900px',
        margin: '50px auto 0',
        boxShadow: '0 6px 20px rgba(0,0,0,0.05)',
      }}>
        <h3 style={{
          fontSize: '1.4rem',
          fontWeight: '600',
          marginBottom: '15px',
          color: '#0077b6',
        }}>
          Why Choose Our Outsourced Revenue Cycle Management Services?
        </h3>
        <p style={{
          fontSize: '1.05rem',
          lineHeight: '1.8',
          color: '#555',
          marginBottom: '25px',
        }}>
          MDeRCM combines industry expertise with AI technology to deliver{' '}
          <Link 
            to="/healthcare/ai" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          >
            healthcare cost reduction strategies
          </Link>{' '}
          that work. Our <strong>medical billing outsourcing companies</strong> team has helped specialty practices reduce claim denials by up to 40% and accelerate reimbursement cycles by 3x.
        </p>

        {/* Feature Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginTop: '25px',
        }}>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#0077b6', marginBottom: '8px' }}>
              🎯 Specialty Expertise
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Deep knowledge of dental CDT codes and mental health ICD-10 coding
            </p>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#0077b6', marginBottom: '8px' }}>
              ⚡ AI Automation
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              <Link to="/healthcare/ai" style={{ color: '#0077b6' }}>Advanced AI tools</Link> for faster claim processing
            </p>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#0077b6', marginBottom: '8px' }}>
              🔒 HIPAA Compliant
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              <Link to="/compliance" style={{ color: '#0077b6' }}>ISO certified</Link> with full data security
            </p>
          </div>
        </div>
      </div>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        viewport={{ once: true }}
        style={{ textAlign: 'center', marginTop: '50px' }}
      >
        <a
          href="#roi-calculator"
          style={{
            backgroundColor: '#F26522',
            color: '#fff',
            padding: '16px 32px',
            fontSize: '1.05rem',
            fontWeight: '600',
            borderRadius: '30px',
            textDecoration: 'none',
            boxShadow: '0 6px 18px rgba(242, 101, 34, 0.3)',
            display: 'inline-block',
            maxWidth: '100%',
            boxSizing: 'border-box',
            whiteSpace: 'normal',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#d3541c';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(242, 101, 34, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#F26522';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 18px rgba(242, 101, 34, 0.3)';
          }}
        >
          Calculate Your RCM Savings - See ROI in 60 Seconds
        </a>
      </motion.div>

      {/* Mobile Responsive Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            section {
              padding: 60px 4%;
            }

            div[style*='flex: 1 1 450px'] {
              flex: 1 1 100% !important;
              max-width: 100% !important;
            }

            ul {
              font-size: 0.98rem !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default SpecialtiesSection;